// Stateless URL encoding for sharable quiz results.
//
// The result page (/quiz/result) and the OG image endpoint
// (/api/og/quiz-result) both read these same params, so the page
// + the image stay in sync from the URL alone — no DB lookup, no
// per-result row to persist, no privacy concern.
//
//   x          compass X position, -1.000 .. +1.000
//   y          compass Y position, -1.000 .. +1.000
//   top        single candidate id of the closest match
//   ts         closest-match score 0..100 (optional)
//   bottom     single candidate id of the furthest match (optional)
//   bs         furthest-match score 0..100 (optional)
//
// Earlier iteration used `matches=id1,id2,id3` for top-3. We pared
// down to closest + furthest per the editor's request so the share
// card focuses on the contrast (most-like vs least-like).

export interface ShareState {
  x: number;
  y: number;
  // Top three closest matches (top is required, t2 / t3 optional).
  topId: number;
  topName: string | null;
  topParty: string | null;
  topScore: number | null;
  top2Id: number | null;
  top2Name: string | null;
  top2Party: string | null;
  top2Score: number | null;
  top3Id: number | null;
  top3Name: string | null;
  top3Party: string | null;
  top3Score: number | null;
  // Furthest match (single).
  bottomId: number | null;
  bottomName: string | null;
  bottomParty: string | null;
  bottomScore: number | null;
}

/** Encode a result into URL search-param string (no leading "?").
 *  Name + party are included so the OG image renderer can build the
 *  two match cards without a cross-runtime fetch back to /api/quiz —
 *  that fetch was producing empty cards in the wild when the Edge
 *  endpoint couldn't reach the Node Lambda in time. */
export function encodeShareState(s: ShareState): string {
  const out: Record<string, string> = {
    x: clamp(s.x, -1, 1).toFixed(3),
    y: clamp(s.y, -1, 1).toFixed(3),
    top: String(s.topId),
  };
  if (s.topName) out.tn = s.topName;
  if (s.topParty) out.tp = s.topParty;
  if (s.topScore != null && Number.isFinite(s.topScore)) {
    out.ts = String(Math.max(0, Math.min(100, Math.round(s.topScore))));
  }
  if (s.top2Id != null) {
    out.t2 = String(s.top2Id);
    if (s.top2Name) out.t2n = s.top2Name;
    if (s.top2Party) out.t2p = s.top2Party;
    if (s.top2Score != null && Number.isFinite(s.top2Score)) {
      out.t2s = String(Math.max(0, Math.min(100, Math.round(s.top2Score))));
    }
  }
  if (s.top3Id != null) {
    out.t3 = String(s.top3Id);
    if (s.top3Name) out.t3n = s.top3Name;
    if (s.top3Party) out.t3p = s.top3Party;
    if (s.top3Score != null && Number.isFinite(s.top3Score)) {
      out.t3s = String(Math.max(0, Math.min(100, Math.round(s.top3Score))));
    }
  }
  if (s.bottomId != null && Number.isFinite(s.bottomId)) {
    out.bottom = String(s.bottomId);
  }
  if (s.bottomName) out.bn = s.bottomName;
  if (s.bottomParty) out.bp = s.bottomParty;
  if (s.bottomScore != null && Number.isFinite(s.bottomScore)) {
    out.bs = String(Math.max(0, Math.min(100, Math.round(s.bottomScore))));
  }
  return new URLSearchParams(out).toString();
}

/** Parse params (object or URLSearchParams) into a ShareState, or null
 *  if any required field is missing / out of range. */
export function decodeShareState(
  params: URLSearchParams | Record<string, string | undefined>,
): ShareState | null {
  const get = (k: string): string | undefined =>
    params instanceof URLSearchParams ? params.get(k) ?? undefined : params[k];

  const xRaw = get('x');
  const yRaw = get('y');
  const topRaw = get('top') ?? firstFromLegacy(get('matches'));
  if (xRaw == null || yRaw == null || topRaw == null) return null;

  const x = Number(xRaw);
  const y = Number(yRaw);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (x < -1.5 || x > 1.5 || y < -1.5 || y > 1.5) return null;

  const topId = parseInt(topRaw, 10);
  if (!Number.isFinite(topId) || topId <= 0) return null;

  const topScore = parseScore(get('ts'));
  const topName = parseStr(get('tn'));
  const topParty = parseStr(get('tp'));
  const top2Id = parseId(get('t2'));
  const top2Name = parseStr(get('t2n'));
  const top2Party = parseStr(get('t2p'));
  const top2Score = parseScore(get('t2s'));
  const top3Id = parseId(get('t3'));
  const top3Name = parseStr(get('t3n'));
  const top3Party = parseStr(get('t3p'));
  const top3Score = parseScore(get('t3s'));
  const bottomId = parseId(get('bottom'));
  const bottomScore = parseScore(get('bs'));
  const bottomName = parseStr(get('bn'));
  const bottomParty = parseStr(get('bp'));

  return {
    x: clamp(x, -1, 1),
    y: clamp(y, -1, 1),
    topId,
    topName,
    topParty,
    topScore,
    top2Id,
    top2Name,
    top2Party,
    top2Score,
    top3Id,
    top3Name,
    top3Party,
    top3Score,
    bottomId,
    bottomName,
    bottomParty,
    bottomScore,
  };
}

function parseStr(raw: string | undefined): string | null {
  if (raw == null) return null;
  const s = raw.trim();
  if (!s) return null;
  // Cap to keep URL within reason
  return s.slice(0, 80);
}

function parseId(raw: string | undefined): number | null {
  if (raw == null) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseScore(raw: string | undefined): number | null {
  if (raw == null) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

function firstFromLegacy(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const first = raw.split(',')[0]?.trim();
  return first || undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** Canonical origin used in shareable URLs and og:image / og:url meta
 *  tags. Hardcoded so the same URL is used regardless of how the
 *  visitor arrived — direct on the custom domain, iframe-embedded on
 *  lovinmalta.com, or the Vercel default URL. */
export const SHARE_ORIGIN = 'https://elections.lovinmalta.com';

/** Whether the share UI (Finish button on Matrix, /quiz/result page,
 *  the share button itself) is exposed. Previously gated to Vercel
 *  preview deploys + localhost ("only on develop until launch"); now
 *  always on since the share work has shipped to main. Kept as a
 *  function so the gate is easy to flip back if a regression is
 *  found in the wild. */
export function isShareEnabled(): boolean {
  return true;
}
